import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, DatePicker, Modal, Tooltip, Button, message,
  Badge, Card, Tabs, Row, Col, Space, Select as AntSelect,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, FlagOutlined, LockOutlined, SaveOutlined,
} from '@ant-design/icons';
import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import AccionesDeSeguimiento from 'components/SeguimientoDeRegistro/AccionesDeSeguimiento';
import AdjuntosDeSeguimiento from 'components/SeguimientoDeRegistro/AdjuntosDeSeguimiento';
import HiloDeSeguimiento from 'components/SeguimientoDeRegistro/HiloDeSeguimiento';
import ListaDeSeguimientos from 'components/SeguimientoDeRegistro/ListaDeSeguimientos';
import { IconoDeSeguimiento } from 'utils/iconosDeSeguimiento';
import { getEmpleadosOptimized } from 'api/estructuraOrganizacional/empleados';
import { getAdministracionDePlazasvistaOptimized } from 'api/catalogos/admnistracionDePlazas';

const baseURISeguimientos = '/configuraciones/seguimientos-de-registros/';
const baseURIIconografiaDeSeguimiento = '/configuraciones/iconografias-de-seguimiento/';

// Máximo de ids que se mandan en la URL (object_id_in) antes de filtrar solo por tipo de registro.
const MAX_IDS_EN_URL = 100;

const toArray = (d) => (Array.isArray(d) ? d : (d?.results ?? []));

const rules = {
  required: [{ required: true, message: 'El campo es requerido' }],
};

// Separación (px) entre la ventana de la nota y el menú lateral / el borde derecho.
const MARGEN_DEL_MARCO = 40;

// Mide el menú lateral y el título de la pantalla para dejarlos visibles y centrar la
// ventana en el área de contenido, sea cual sea el tamaño del menú o el zoom del navegador.
const medirMarco = () => {
  const sider = document.querySelector('.ant-layout-sider');
  const titulo = document.querySelector('.breadbrumb-title');
  return {
    izquierda: sider ? Math.round(sider.getBoundingClientRect().right) : 0,
    arriba: titulo ? Math.round(titulo.getBoundingClientRect().bottom) + 14 : 115,
  };
};

// Aspecto de las tarjetas: bordes redondeados y una sombra ligera para dar relieve.
const ESTILO_TARJETA = { borderRadius: 8, boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)' };

// Sección con título dentro de una pestaña: agrupa campos relacionados.
function Seccion({ titulo, children }) {
  return (
    <Card
      size="small"
      title={titulo}
      style={{ marginBottom: 12, borderRadius: 8 }}
      headStyle={{ background: '#fafafa', fontWeight: 600 }}
    >
      {children}
    </Card>
  );
}

Seccion.propTypes = {
  titulo: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const opcionesSiNo = [
  { value: false, label: 'NO' },
  { value: true, label: 'SÍ' },
];

// Mapea cada campo requerido a la pestaña donde vive, para saltar a ella si falla la validación.
const requiredFormStructure = [
  { tabKey: 'general', tabFields: ['iconografia', 'responsable_de_seguimiento'] },
  { tabKey: 'acciones', tabFields: ['se_requiere', 'fecha_de_registro', 'fecha_compromiso'] },
];

// Mismo mecanismo de "Iconografía de Seguimiento" ya construido para
// Conciliación Bancaria (ver AvanceConciliacionTab.jsx). Soporta dos modos,
// combinables en la misma instancia del hook:
// - Modo lista: `registros` + `seleccionIds` (grid externo) → columna
//   "Seguimiento" + botón genérico en `customActions` que abre la modal con
//   un Select para elegir el ícono.
// - Modo detalle: `registroActivo` (un único registro, el que se está
//   editando dentro de su propia vista de detalle) → `barraIconos`, una
//   barra vertical de íconos (uno por catálogo) que aplica directo al dar
//   clic, sin pasar por el Select — igual que `renderBarraIconos()` en
//   AvanceConciliacionTab.jsx.
export default function useSeguimientoDeRegistro({
  contentTypeAppLabel,
  contentTypeModel,
  moduloDescripcion,
  documentoOperativoDescripcion,
  moduloOrigenLabel,
  tipoDeRegistroLabel,
  registros,
  seleccionIds = [],
  registroActivo = null,
  mutable,
  getRegistroLabel,
}) {
  const [form] = Form.useForm();
  const [iconografias, setIconografias] = useState([]);
  const [empleadosCatalog, setEmpleadosCatalog] = useState([]);
  const [adminPlazasCatalog, setAdminPlazasCatalog] = useState([]);
  const [aplicados, setAplicados] = useState([]);
  const [filaActiva, setFilaActiva] = useState(null);
  const [registroEditando, setRegistroEditando] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [tabActiva, setTabActiva] = useState('general');
  const [marco, setMarco] = useState({ izquierda: 0, arriba: 115 });
  const [tabDerecha, setTabDerecha] = useState('conversacion');
  const [listaFila, setListaFila] = useState(null);
  // Contador que sube cada vez que cambia algo en la nota (mensaje, adjunto, cierre) para que
  // la conversación, la tabla y los adjuntos se actualicen entre sí sin recargar la pantalla.
  const [refresco, setRefresco] = useState(0);
  const notificarCambio = () => setRefresco((r) => r + 1);
  const [adjuntoSeleccionado, setAdjuntoSeleccionado] = useState(null);

  // El emisor es quien creó la nota; al crear una nueva, el usuario actual es el emisor.
  const esEmisor = registroEditando ? !!registroEditando.es_emisor : true;
  const cerrada = !!registroEditando?.se_cierra_la_nota;

  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const [resIconografias, resEmpleados, resAdminPlazas] = await Promise.all([
          API.get(baseURIIconografiaDeSeguimiento, {
            params: {
              modulo_descripcion: moduloDescripcion,
              documento_operativo_descripcion: documentoOperativoDescripcion,
              estados_globales: 1,
            },
          }),
          getEmpleadosOptimized(),
          getAdministracionDePlazasvistaOptimized(),
        ]);
        setIconografias(toArray(resIconografias.data));
        setEmpleadosCatalog((resEmpleados || []).map((emp) => ({
          id: emp.id,
          descripcion: emp.numero_y_nombre_completo
            || `${emp.numero_de_empleado || ''} - ${emp.nombre || ''} ${emp.apellido_paterno || ''} ${emp.apellido_materno || ''}`.trim(),
        })));
        setAdminPlazasCatalog(resAdminPlazas || []);
      } catch (err) {
        onError(err);
      }
    };
    fetchCatalogos();
    // eslint-disable-next-line
  }, [moduloDescripcion, documentoOperativoDescripcion]);

  // Al abrir la ventana (y si cambia el tamaño de la pantalla) se vuelve a medir el marco.
  useEffect(() => {
    if (!modalVisible) return undefined;
    const actualizar = () => setMarco(medirMarco());
    actualizar();
    window.addEventListener('resize', actualizar);
    return () => window.removeEventListener('resize', actualizar);
  }, [modalVisible]);

  const refrescar = async () => {
    try {
      const idsBase = (registros || []).map((r) => r.id);
      const ids = registroActivo?.id && !idsBase.includes(registroActivo.id)
        ? [...idsBase, registroActivo.id]
        : idsBase;
      if (!ids.length) {
        setAplicados([]);
        return;
      }
      // En listados grandes (p. ej. Empleados) enviar todos los ids en la URL la
      // desbordaría; como solo los registros con ícono aplicado tienen seguimiento,
      // basta con pedir los del tipo de registro y cruzarlos en el cliente.
      const params = {
        content_type_app_label: contentTypeAppLabel,
        content_type_model: contentTypeModel,
      };
      if (ids.length <= MAX_IDS_EN_URL) params.object_id_in = ids.join(',');
      const res = await API.get(baseURISeguimientos, { params });
      setAplicados(toArray(res.data));
    } catch (err) {
      onError(err);
    }
  };

  useEffect(() => {
    refrescar();
    // eslint-disable-next-line
  }, [registros, registroActivo?.id, contentTypeAppLabel, contentTypeModel]);

  // Un registro puede tener varias notas: de más reciente a más antigua.
  const notasDe = (objectId) => aplicados
    .filter((r) => r.object_id === objectId)
    .sort((a, b) => b.id - a.id);
  // La nota activa más reciente del registro (la que se resume con un ícono).
  const buscarAplicado = (objectId) => notasDe(objectId).find((n) => !n.se_cierra_la_nota) || null;

  const filaObjetivo = registroActivo || (registros || []).find((r) => r.id === seleccionIds[0]);
  const seleccionValida = registroActivo ? true : seleccionIds.length === 1;

  const onChangeResponsableDeSeguimiento = (empleadoId) => {
    if (empleadoId) {
      const adminPlazaEmp = adminPlazasCatalog?.find(
        (admP) => String(admP?.empleado_id) === String(empleadoId)
          || String(admP?.empleado) === String(empleadoId),
      );
      form.setFieldsValue({
        plaza_descripcion: adminPlazaEmp?.puesto_clave_y_descripcion || '',
        area_descripcion: adminPlazaEmp?.directorio_area_clave_y_nombre || '',
      });
    } else {
      form.setFieldsValue({ plaza_descripcion: null, area_descripcion: null });
    }
  };

  const onCancel = () => {
    setModalVisible(false);
    setFilaActiva(null);
    setRegistroEditando(null);
    setTabActiva('general');
    setTabDerecha('conversacion');
    setAdjuntoSeleccionado(null);
    form.resetFields();
  };

  const cargarEnFormulario = (existente) => {
    form.setFieldsValue(formatReceived({
      iconografia: existente.iconografia,
      responsable_de_seguimiento: existente.responsable_de_seguimiento,
      se_requiere: existente.se_requiere,
      fecha_de_registro: existente.fecha_de_registro,
      fecha_compromiso: existente.fecha_compromiso,
      fecha_de_atencion: existente.fecha_de_atencion,
      acciones_realizadas: existente.acciones_realizadas,
      comentario_de_cierre: existente.comentario_de_cierre,
      observaciones: existente.observaciones,
      se_cierra_la_nota: !!existente.se_cierra_la_nota,
      fecha_de_cierre_de_la_nota: existente.fecha_de_cierre_de_la_nota,
    }));
    // Plaza/Área son derivados en el cliente a partir del empleado (nunca se
    // guardan en el backend, ver onFinish), así que al reabrir un registro
    // existente hay que recalcularlos aquí también — si no, el campo
    // responsable_de_seguimiento carga bien pero Plaza/Área quedan vacíos.
    onChangeResponsableDeSeguimiento(existente.responsable_de_seguimiento);
  };

  const abrirExistente = (row, existente) => {
    setFilaActiva(row);
    setRegistroEditando(existente);
    cargarEnFormulario(existente);
    setModalVisible(true);
  };

  // Clic en Seguimientos: muestra la lista de notas del registro (activas y cerradas).
  const abrirSeguimiento = () => {
    if (!seleccionValida || !filaObjetivo) return;
    setListaFila(filaObjetivo);
  };

  // "Agregar" dentro de la lista: nota nueva sobre el mismo registro.
  const abrirNuevo = () => {
    setFilaActiva(listaFila);
    setRegistroEditando(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Modo detalle: clic directo en un ícono de la barra vertical, sin pasar
  // por el Select de la modal. Crea una nota nueva (un registro puede tener varias).
  const abrirConIcono = (icono) => {
    if (!seleccionValida || !filaObjetivo) return;
    setFilaActiva(filaObjetivo);
    setRegistroEditando(null);
    form.resetFields();
    form.setFieldsValue({ iconografia: icono.id });
    setModalVisible(true);
  };

  const onFinish = async () => {
    try {
      setGuardando(true);
      await form.validateFields();
      const values = getFormattedValues(form.getFieldsValue());
      delete values.plaza_descripcion;
      delete values.area_descripcion;
      delete values.fecha_de_cierre_de_la_nota; // la asigna el servidor al cerrar la nota
      if (!esEmisor) {
        // Solo el emisor puede cerrar la nota y capturar el comentario de cierre.
        delete values.se_cierra_la_nota;
        delete values.comentario_de_cierre;
      }
      const payload = {
        ...values,
        content_type_app_label: contentTypeAppLabel,
        content_type_model: contentTypeModel,
        object_id: filaActiva.id,
      };
      const res = registroEditando
        ? await API.put(`${baseURISeguimientos}${registroEditando.id}/`, payload)
        : await API.post(baseURISeguimientos, payload);
      if (res?.status === 201 || res?.status === 200) {
        onSuccess(res, registroEditando ? 'Actualizado correctamente.' : 'Iconografía aplicada correctamente.');
        if (registroEditando) {
          onCancel();
        } else {
          // Nota recién creada: se queda abierta para capturar sus acciones y adjuntos.
          setRegistroEditando(res.data);
          cargarEnFormulario(res.data);
        }
        await refrescar();
      }
      setGuardando(false);
    } catch (err) {
      const campoConError = err?.errorFields?.[0]?.name?.[0];
      const estructura = requiredFormStructure.find((e) => e.tabFields.includes(campoConError));
      if (estructura) {
        setTabActiva(estructura.tabKey);
        message.warn('Favor de corregir los errores señalados');
        setGuardando(false);
      } else {
        onError(err, setGuardando);
      }
    }
  };

  const registroRelacionadoTexto = filaActiva
    ? (registroEditando?.registro_relacionado || getRegistroLabel?.(filaActiva) || `Registro ${filaActiva.id}`)
    : '';

  const columnaSeguimiento = {
    titleText: 'Seguimientos',
    dataIndex: 'id',
    key: 'seguimiento_de_registro',
    width: 110,
    render: (val, record) => {
      const notas = notasDe(val);
      if (!notas.length) return <span style={{ color: '#bfbfbf' }}>—</span>;
      const activas = notas.filter((n) => !n.se_cierra_la_nota);
      const cerradas = notas.length - activas.length;
      const reciente = activas[0];
      return (
        <Tooltip
          title={reciente
            ? `${reciente.iconografia_descripcion}${activas.length > 1 ? ` (+${activas.length - 1} más)` : ''}`
            : `${cerradas} ${cerradas === 1 ? 'nota cerrada' : 'notas cerradas'}`}
        >
          <Button
            type="text"
            size="small"
            onClick={() => setListaFila(record || { id: val })}
            style={{ padding: 2, height: 'auto' }}
          >
            <Badge count={activas.length > 1 ? activas.length : 0} size="small" offset={[4, -2]}>
              {reciente
                ? <IconoDeSeguimiento name={reciente.iconografia_icono} style={{ fontSize: 16 }} />
                : <LockOutlined style={{ fontSize: 16, color: '#bfbfbf' }} />}
            </Badge>
          </Button>
        </Tooltip>
      );
    },
  };

  const accionSeguimiento = {
    icon: <FlagOutlined />,
    text: 'Seguimientos',
    onClick: abrirSeguimiento,
    disabled: !mutable || !seleccionValida,
    disabledBySelection: false,
  };

  // Modo detalle: si `registroActivo` ya tiene una iconografía aplicada, se
  // muestra su ícono (clicable para editarla) — para que quede visible
  // dentro de la propia vista de detalle y no solo en la columna del grid
  // externo.
  const registroAplicadoActivo = filaObjetivo ? buscarAplicado(filaObjetivo.id) : null;

  const notasDelRegistroActivo = filaObjetivo ? notasDe(filaObjetivo.id) : [];
  const activasDelRegistroActivo = notasDelRegistroActivo.filter((n) => !n.se_cierra_la_nota);

  const indicadorSeguimiento = notasDelRegistroActivo.length ? (
    <Tooltip title="Ver los seguimientos de este registro">
      <Button
        type="text"
        size="small"
        onClick={() => setListaFila(filaObjetivo)}
        style={{ padding: 4, height: 'auto' }}
      >
        <Badge count={activasDelRegistroActivo.length} size="small" offset={[4, -2]}>
          {registroAplicadoActivo
            ? (
              <IconoDeSeguimiento
                name={registroAplicadoActivo.iconografia_icono}
                style={{ fontSize: 18 }}
              />
            )
            : <LockOutlined style={{ fontSize: 18, color: '#bfbfbf' }} />}
        </Badge>
      </Button>
    </Tooltip>
  ) : null;

  // Modo detalle: barra vertical de íconos para colocar junto a la tabla de
  // trabajo de la vista de detalle (ver `registroActivo`). Solo tiene
  // sentido cuando el hook se usa en ese modo; en modo lista simplemente
  // queda deshabilitada por falta de selección válida.
  const barraIconos = (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: 96,
      marginRight: 8,
      paddingRight: 6,
      borderRight: '1px solid #f0f0f0',
    }}
    >
      <Tooltip title="Seleccione un registro y elija un ícono para aplicarle un seguimiento" placement="right">
        <FlagOutlined style={{ padding: 4, marginBottom: 4, color: '#8c8c8c' }} />
      </Tooltip>
      {iconografias.map((icono) => (
        <Tooltip key={icono.id} title={icono.descripcion} placement="right">
          <Button
            type="text"
            size="small"
            disabled={!mutable || !seleccionValida}
            onClick={() => abrirConIcono(icono)}
            style={{ padding: 4, height: 'auto' }}
          >
            <IconoDeSeguimiento name={icono.icono} style={{ fontSize: 16 }} />
          </Button>
        </Tooltip>
      ))}
    </div>
  );

  // Pantalla principal: coloca la barra de íconos a la izquierda de la tabla. Se elige un
  // registro, se da clic en un ícono y se abre la ventana de la nota con ese ícono ya puesto
  // (como la banderita de seguimiento de Conciliación Bancaria).
  const conBarra = (children) => (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {barraIconos}
      <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    </div>
  );

  // Modo detalle: envuelve el contenido de la vista de detalle (normalmente sus
  // pestañas) con la barra vertical de íconos a la derecha y, arriba, la línea
  // "Seguimiento de <etiqueta>: ..." con el ícono ya aplicado (o la indicación de
  // elegir uno). Así el seguimiento se ve en cualquier pestaña del detalle, igual
  // que en Solicitud de Cuentas Internas, sin depender del botón "Seguimiento".
  const envolverConSeguimiento = (children, etiqueta = 'este registro') => {
    if (!registroActivo?.id) return children;
    return (
      <div>
        <div style={{ marginBottom: 8 }}>
          {`Seguimientos de ${etiqueta}: `}
          {indicadorSeguimiento || (
            <span style={{ color: '#bfbfbf' }}>Sin seguimientos</span>
          )}
        </div>
        {children}
      </div>
    );
  };

  // El hilo cierra/reabre la nota por su cuenta: aquí solo se sincroniza el estado local.
  const onNotaActualizada = (nota) => {
    setRegistroEditando(nota);
    cargarEnFormulario(nota);
    refrescar();
  };

  // "Siguiente": valida los campos requeridos de General y pasa a la pestaña Acciones.
  const irASiguiente = async () => {
    if (esEmisor && !cerrada) {
      try {
        await form.validateFields(requiredFormStructure[0].tabFields);
      } catch (err) {
        message.warn('Complete los campos requeridos para continuar');
        return;
      }
    }
    setTabActiva('acciones');
  };

  const tabGeneral = (
    <>
      <Seccion titulo="Registro">
        <Form.Item label="Módulo de Origen">
          <Input value={moduloOrigenLabel} disabled />
        </Form.Item>
        {tipoDeRegistroLabel && (
        <Form.Item label="Tipo de Registro">
          <Input value={tipoDeRegistroLabel} disabled />
        </Form.Item>
        )}
        <Form.Item label="Registro Relacionado">
          <Input value={registroRelacionadoTexto} disabled />
        </Form.Item>
      </Seccion>
      <Seccion titulo="Seguimiento">
        <Form.Item name="iconografia" label="Ícono de Seguimiento" rules={rules.required}>
          <Select
            dataSource={iconografias}
            valueProp="id"
            labelProp="descripcion"
            render={(item) => (
              <>
                <IconoDeSeguimiento name={item.icono} style={{ marginRight: 8 }} />
                {item.descripcion}
              </>
            )}
            showSearch
          />
        </Form.Item>
        <Form.Item
          name="responsable_de_seguimiento"
          label="Responsable de Seguimiento"
          rules={rules.required}
        >
          <Select
            dataSource={empleadosCatalog}
            keyProp="id"
            labelProp="descripcion"
            onChange={onChangeResponsableDeSeguimiento}
            showSearch
            allowClear
          />
        </Form.Item>
        <Form.Item name="plaza_descripcion" label="Plaza">
          <Input disabled />
        </Form.Item>
        <Form.Item name="area_descripcion" label="Área">
          <Input disabled />
        </Form.Item>
      </Seccion>
      <div style={{ textAlign: 'right' }}>
        <Button type="primary" icon={<ArrowRightOutlined />} onClick={irASiguiente}>
          Siguiente
        </Button>
      </div>
    </>
  );

  const tabAcciones = (
    <>
      <Seccion titulo="Solicitud">
        <Form.Item name="se_requiere" label="Se Requiere" rules={rules.required}>
          <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
        <Row gutter={10}>
          <Col xs={24} sm={12} xxl={8}>
            <Form.Item name="fecha_de_registro" label="Fecha de Registro" rules={rules.required}>
              <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xxl={8}>
            <Form.Item name="fecha_compromiso" label="Fecha Compromiso" rules={rules.required}>
              <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} xxl={8}>
            <Form.Item name="fecha_de_atencion" label="Fecha de Atención">
              <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
      </Seccion>
      <Seccion titulo="Atención">
        <Form.Item name="acciones_realizadas" label="Acción(es) realizada(s)">
          <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
        <Form.Item name="observaciones" label="Observaciones">
          <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
        <Form.Item
          name="comentario_de_cierre"
          label="Comentario de Cierre"
        >
          <Input.TextArea
            allowClear
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={!esEmisor}
          />
        </Form.Item>
      </Seccion>
      <Seccion titulo="Cierre">
        <Row gutter={10}>
          <Col xs={24} md={8}>
            <Form.Item
              name="se_cierra_la_nota"
              label="Se Cierra la Nota"
            >
              <AntSelect options={opcionesSiNo} disabled={!esEmisor} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="fecha_de_cierre_de_la_nota"
              label="Fecha de Cierre de la Nota"
            >
              <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Estatus">
              <Input value={registroEditando?.estados_globales_descripcion || 'Activo'} disabled />
            </Form.Item>
          </Col>
        </Row>
      </Seccion>
      <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => setTabActiva('general')}>
          Anterior
        </Button>
        {esEmisor && (
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onFinish}
            loading={guardando}
          >
            Guardar
          </Button>
        )}
      </Space>
    </>
  );

  // Clic en un documento dentro de la conversación: se abre en el visor de Adjuntos.
  const verAdjunto = (adjunto) => {
    setAdjuntoSeleccionado(adjunto.id);
    setTabDerecha('adjuntos');
  };

  // Panel derecho: la conversación, la tabla de acciones (dos vistas del mismo historial)
  // y los adjuntos con su visor.
  const panelDerecho = (
    <Card
      bodyStyle={{ padding: 12 }}
      bordered={false}
      style={ESTILO_TARJETA}
      className="panel-seguimiento"
    >
      {/* Alto fijo a las pestañas: las tablas miden su alto contra este contenedor. */}
      <style>
        {`.panel-seguimiento .ant-tabs-content-holder {
          height: calc(100vh - 340px);
          min-height: 420px;
          overflow: auto;
        }`}
      </style>
      <Tabs
        activeKey={tabDerecha}
        onChange={setTabDerecha}
        items={[
          {
            key: 'conversacion',
            label: 'Conversación',
            children: (
              <HiloDeSeguimiento
                seguimiento={registroEditando}
                onNotaActualizada={onNotaActualizada}
                onVerAdjunto={verAdjunto}
                refresco={refresco}
                onCambio={notificarCambio}
              />
            ),
          },
          {
            key: 'tabla',
            label: 'Registros',
            children: (
              <AccionesDeSeguimiento
                seguimiento={registroEditando}
                esEmisor={esEmisor}
                cerrada={cerrada}
                refresco={refresco}
                onCambio={notificarCambio}
              />
            ),
          },
          {
            key: 'adjuntos',
            label: 'Adjuntos',
            children: (
              <AdjuntosDeSeguimiento
                seguimientoId={registroEditando?.id}
                cerrada={cerrada}
                seleccionId={adjuntoSeleccionado}
                onSeleccion={setAdjuntoSeleccionado}
                refresco={refresco}
                onCambio={notificarCambio}
              />
            ),
          },
        ]}
      />
    </Card>
  );

  const modalNota = (
    <Modal
      open={modalVisible}
      title={(
        <FormSubmitControls
          label={registroEditando ? 'Editar Iconografía de Seguimiento' : 'Aplicar Iconografía de Seguimiento'}
          onCancel={onCancel}
          loading={guardando}
        />
      )}
      onCancel={onCancel}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={!guardando}
      width={`calc(100vw - ${marco.izquierda + MARGEN_DEL_MARCO * 2}px)`}
      style={{ top: marco.arriba, margin: `0 0 0 ${marco.izquierda + MARGEN_DEL_MARCO}px`, paddingBottom: 0 }}
      bodyStyle={{
        height: `calc(100vh - ${marco.arriba + 56 + MARGEN_DEL_MARCO}px)`,
        overflowY: 'auto',
        background: '#f5f5f5',
      }}
      maskStyle={{ background: 'rgba(0, 0, 0, 0.25)' }}
      forceRender
      destroyOnClose
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={8}>
          <Form
            name="seguimiento_de_registro"
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            initialValues={{ se_cierra_la_nota: false }}
            disabled={cerrada || !esEmisor}
          >
            <Card bordered={false} style={ESTILO_TARJETA}>
              <Tabs
                activeKey={tabActiva}
                onChange={setTabActiva}
                items={[
                  {
                    key: 'general', label: 'General', forceRender: true, children: tabGeneral,
                  },
                  {
                    key: 'acciones', label: 'Acciones', forceRender: true, children: tabAcciones,
                  },
                ]}
              />
            </Card>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
        <Col xs={24} xl={16}>
          {panelDerecho}
        </Col>
      </Row>
    </Modal>
  );

  // Persona del registro cuya lista se está viendo (nombre, plaza y área), tomada de la fila de
  // la tabla; en las pantallas sin persona (Plazas, Nóminas…) se usa la etiqueta del registro.
  const personaDeLaLista = listaFila ? {
    // Solo el nombre: sin el número de empleado ni el guion ("6796 - NOMBRE" -> "NOMBRE").
    nombre: [listaFila.nombre, listaFila.apellido_paterno, listaFila.apellido_materno]
      .filter(Boolean).join(' ')
      || (listaFila.numero_y_nombre_completo || listaFila.empleado_numero_y_nombre_completo || '')
        .replace(/^\s*\S+\s+-\s+/, '')
      || null,
    plaza: listaFila.plaza_clave_y_descripcion || listaFila.puesto_clave_y_descripcion || null,
    area: listaFila.directorio_area_clave_y_nombre
      || listaFila.directorio_de_area_clave_y_nombre || null,
  } : {};
  const etiquetaDeLaLista = listaFila
    ? (personaDeLaLista.nombre || getRegistroLabel?.(listaFila) || `Registro ${listaFila.id}`)
    : '';
  const detalleDeLaLista = [personaDeLaLista.plaza, personaDeLaLista.area]
    .filter(Boolean).join(' · ');

  const modalSeguimiento = (
    <>
      <ListaDeSeguimientos
        open={!!listaFila}
        titulo={`Seguimiento de ${etiquetaDeLaLista}`}
        subtitulo={detalleDeLaLista || null}
        notas={listaFila ? notasDe(listaFila.id) : []}
        mutable={!!mutable}
        onAgregar={abrirNuevo}
        onEditar={(nota) => abrirExistente(listaFila, nota)}
        onCerrar={() => setListaFila(null)}
      />
      {modalNota}
    </>
  );

  return {
    columnaSeguimiento,
    accionSeguimiento,
    modalSeguimiento,
    barraIconos,
    conBarra,
    envolverConSeguimiento,
    indicadorSeguimiento,
    registroAplicadoActivo,
    refrescar,
  };
}
